import { useEffect, useState, type MouseEvent } from 'react';
import type { ProductMediaDto } from '../api/types';

interface ProductImageGalleryProps {
  media: ProductMediaDto[];
  productTitle: string;
  selectedVariantImageUrl?: string | null;
}

function isVideoMedia(url?: string | null, kind?: string | null): boolean {
  if (kind === 'Video') return true;
  if (!url) return false;
  return /\.(mp4|webm|mov)(\?.*)?$/i.test(url);
}

export function ProductImageGallery({
  media,
  productTitle,
  selectedVariantImageUrl,
}: ProductImageGalleryProps) {
  // Medya listesi
  const images = media.length > 0 ? media : [];
  const [activeMedia, setActiveMedia] = useState(0);

  // Hover zoom lens durumu
  const [isHovering, setIsHovering] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

  // Tam ekran lightbox modal durumu
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxScale, setLightboxScale] = useState(1);

  // Varyant görseli değiştiğinde aktif görseli güncelle
  useEffect(() => {
    if (selectedVariantImageUrl) {
      const idx = images.findIndex((m) => m.url === selectedVariantImageUrl);
      if (idx !== -1) {
        setActiveMedia(idx);
      }
    }
  }, [selectedVariantImageUrl, images]);

  const currentItem = images[activeMedia];
  const currentImage = currentItem?.url ?? selectedVariantImageUrl ?? null;
  const currentAlt = currentItem?.altText ?? productTitle;
  const currentIsVideo = isVideoMedia(currentImage, currentItem?.kind);

  // Mouse move ile zoom koordinatı hesaplama
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (currentIsVideo) return;
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - left) / width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - top) / height) * 100));
    setZoomPosition({ x, y });
  };

  // Lightbox klavye kontrolleri (ESC, Sol ok, Sağ ok)
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
        setLightboxScale(1);
      } else if (e.key === 'ArrowLeft') {
        setActiveMedia((curr) => (curr > 0 ? curr - 1 : images.length - 1));
        setLightboxScale(1);
      } else if (e.key === 'ArrowRight') {
        setActiveMedia((curr) => (curr < images.length - 1 ? curr + 1 : 0));
        setLightboxScale(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, images.length]);

  return (
    <div className="flex flex-col">
      {/* Ana Görsel / Video ve Hover Zoom Alanı */}
      <div
        className={`group relative aspect-square overflow-hidden rounded-2xl border border-ink-200 select-none ${
          currentIsVideo ? 'bg-ink-950' : 'cursor-crosshair bg-ink-50'
        }`}
        onMouseEnter={() => {
          if (!currentIsVideo) setIsHovering(true);
        }}
        onMouseLeave={() => setIsHovering(false)}
        onMouseMove={handleMouseMove}
        onClick={() => {
          if (currentImage && !currentIsVideo) {
            setIsLightboxOpen(true);
            setLightboxScale(1);
          }
        }}
        title={currentIsVideo ? undefined : 'Tam ekran büyütmek için tıklayın'}
      >
        {currentImage ? (
          currentIsVideo ? (
            <div className="relative h-full w-full flex items-center justify-center bg-black">
              <video
                key={currentImage}
                src={currentImage}
                controls
                playsInline
                preload="metadata"
                className="h-full w-full object-contain"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLightboxOpen(true);
                  setLightboxScale(1);
                }}
                className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1 text-xs font-medium text-white backdrop-blur-xs transition hover:bg-black/85 cursor-pointer"
              >
                <span>Tam Ekran</span>
              </button>
            </div>
          ) : (
            <>
              <img
                src={currentImage}
                alt={currentAlt}
                loading="eager"
                decoding="async"
                className={`h-full w-full object-cover transition-transform duration-150 ease-out will-change-transform ${
                  isHovering ? 'scale-[2.2]' : 'scale-100'
                }`}
                style={
                  isHovering
                    ? {
                        transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                      }
                    : undefined
                }
              />

              {/* Büyüteç İpucu Rozeti */}
              <div className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1 text-xs text-white backdrop-blur-xs transition group-hover:opacity-0">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                  <path d="M11 8v6" />
                  <path d="M8 11h6" />
                </svg>
                <span>Yakınlaştır</span>
              </div>
            </>
          )
        ) : (
          <div className="flex h-full items-center justify-center text-ink-300">Görsel yok</div>
        )}
      </div>

      {/* Küçük Resimler & Videolar (Thumbnails) Şeridi */}
      {images.length > 1 ? (
        <div className="mt-3.5 grid grid-cols-5 gap-2.5 sm:grid-cols-6">
          {images.map((image, index) => {
            const isSelected = index === activeMedia;
            const thumbIsVideo = isVideoMedia(image.url, image.kind);
            return (
              <button
                key={image.id ?? index}
                type="button"
                onClick={() => setActiveMedia(index)}
                aria-label={`${index + 1}. ürün medyası`}
                aria-current={isSelected}
                className={`group relative aspect-square overflow-hidden rounded-xl border-2 transition duration-150 cursor-pointer ${
                  isSelected
                    ? 'border-brand-600 ring-2 ring-brand-100 ring-offset-1'
                    : 'border-ink-200 hover:border-ink-400'
                }`}
              >
                {thumbIsVideo ? (
                  <div className="relative h-full w-full bg-ink-900">
                    <video
                      src={image.url}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-full w-full object-cover opacity-85"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink-900 shadow-md transition group-hover:scale-110">
                        <svg className="h-3.5 w-3.5 ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </span>
                    </div>
                  </div>
                ) : (
                  <img
                    src={image.thumbnailUrl ?? image.url}
                    alt={image.altText ?? `${productTitle} - ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition group-hover:scale-105"
                  />
                )}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Tam Ekran Lightbox / Zoom Dialog */}
      {isLightboxOpen && currentImage ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          onClick={() => {
            setIsLightboxOpen(false);
            setLightboxScale(1);
          }}
        >
          {/* Kontrol Araç Çubuğu */}
          <div
            className="absolute top-4 right-4 z-10 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {!currentIsVideo ? (
              <div className="flex items-center rounded-lg bg-white/10 p-1 text-white backdrop-blur-md">
                <button
                  type="button"
                  onClick={() => setLightboxScale((s) => Math.max(1, s - 0.5))}
                  disabled={lightboxScale <= 1}
                  title="Uzaklaştır (-)"
                  className="rounded p-1.5 hover:bg-white/20 disabled:opacity-40 cursor-pointer"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
                <span className="min-w-[44px] text-center text-xs font-semibold">
                  {Math.round(lightboxScale * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setLightboxScale((s) => Math.min(3.5, s + 0.5))}
                  disabled={lightboxScale >= 3.5}
                  title="Yakınlaştır (+)"
                  className="rounded p-1.5 hover:bg-white/20 disabled:opacity-40 cursor-pointer"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                    <line x1="11" y1="8" x2="11" y2="14" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
                {lightboxScale !== 1 ? (
                  <button
                    type="button"
                    onClick={() => setLightboxScale(1)}
                    className="ml-1 rounded px-2 py-1 text-xs hover:bg-white/20 cursor-pointer"
                  >
                    Sıfırla
                  </button>
                ) : null}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => {
                setIsLightboxOpen(false);
                setLightboxScale(1);
              }}
              title="Kapat (ESC)"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md hover:bg-white/25 cursor-pointer"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Sayfa Göstergesi */}
          {images.length > 1 ? (
            <div className="absolute top-5 left-5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-md">
              {activeMedia + 1} / {images.length}
            </div>
          ) : null}

          {/* Önceki Görsel Butonu */}
          {images.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveMedia((curr) => (curr > 0 ? curr - 1 : images.length - 1));
                setLightboxScale(1);
              }}
              title="Önceki medya (Sol ok)"
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md hover:bg-white/25 cursor-pointer"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : null}

          {/* Büyütülmüş Görsel / Video */}
          <div
            className="flex max-h-[88vh] max-w-[88vw] items-center justify-center overflow-auto p-2 select-none"
            onClick={(e) => e.stopPropagation()}
          >
            {currentIsVideo ? (
              <video
                key={currentImage}
                src={currentImage}
                controls
                autoPlay
                playsInline
                className="max-h-[85vh] max-w-[85vw] rounded-lg bg-black"
              />
            ) : (
              <img
                src={currentImage}
                alt={currentAlt}
                className="max-h-[85vh] max-w-[85vw] object-contain transition-transform duration-200"
                style={{ transform: `scale(${lightboxScale})` }}
              />
            )}
          </div>

          {/* Sonraki Görsel Butonu */}
          {images.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveMedia((curr) => (curr < images.length - 1 ? curr + 1 : 0));
                setLightboxScale(1);
              }}
              title="Sonraki medya (Sağ ok)"
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md hover:bg-white/25 cursor-pointer"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
